import type { FavoriteViewSearch } from '@/contexts/favorites/favoriteView'
import { VpIcon, VpIconButton } from '@vtmn-play/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getFavoriteViewSearchKey } from '@/contexts/favorites/favoriteView'
import { useToasterContext } from '@/contexts/toaster/useToasterContext'
import { useFavoriteAccess } from '@/hooks/favorites/useFavoriteAccess'
import { useDeleteFavoriteView, useFavoriteViews } from '@/hooks/favorites/useFavoriteViews'
import { FavoriteViewPanel } from './FavoriteViewPanel'

interface FavoriteViewActionProps {
  search: FavoriteViewSearch | null
}

export function FavoriteViewAction({ search }: Readonly<FavoriteViewActionProps>) {
  const { t } = useTranslation()
  const { showToast } = useToasterContext()
  const hasFavoriteAccess = useFavoriteAccess()
  const favoritesQuery = useFavoriteViews(hasFavoriteAccess)
  const deleteFavorite = useDeleteFavoriteView()
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  const currentSearchKey = search ? getFavoriteViewSearchKey(search) : null
  const currentFavorite = currentSearchKey
    ? favoritesQuery.data?.items.find(view => getFavoriteViewSearchKey(view.search) === currentSearchKey)
    : undefined

  const handleClick = () => {
    if (!search || deleteFavorite.isPending)
      return

    if (currentFavorite) {
      deleteFavorite.mutate(currentFavorite.id, {
        onSuccess: () => showToast({ variant: 'success', message: t('favorite-views.remove-success') }),
        onError: () => showToast({ variant: 'error', message: t('favorite-views.errors.remove') }),
      })
      return
    }

    setIsPanelOpen(true)
  }

  if (!hasFavoriteAccess)
    return null

  return (
    <>
      <VpIconButton
        className={`favorite-view-toggle${currentFavorite ? ' favorite-view-toggle--active' : ''}`}
        aria-label={t(currentFavorite ? 'favorite-views.remove-current' : 'favorite-views.save-current')}
        aria-pressed={Boolean(currentFavorite)}
        disabled={!search || deleteFavorite.isPending}
        onClick={handleClick}
        data-testid="favorite-view-toggle"
        size="medium"
        variant="tertiary"
      >
        <VpIcon name={currentFavorite ? 'heart-filled' : 'heart'} />
      </VpIconButton>
      {isPanelOpen && (
        <FavoriteViewPanel
          isOpen
          search={search}
          onClose={() => setIsPanelOpen(false)}
        />
      )}
    </>
  )
}
